import { css } from "@emotion/react";
import { useEffect, useImperativeHandle, useRef, useState } from "react";
import { Clickable } from "..";
import { DrawerProps } from "./types";
import { styles } from "./styles";
import { createPortal } from "react-dom";
import { DrawerPushContent } from "./Drawer.PushContent";
import { DrawerPanel } from "./Drawer.Panel";

/**
 * Drawer component that can slide in from the left or right side of the screen.
 * It can either overlay the content or push it to the side.
 */
function Drawer({ onClose, onOpen, ...props }: DrawerProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const panelRef = useRef<HTMLDivElement | null>(null);

  function handleOnOpen() {
    if (typeof props.isOpen === "undefined") {
      setIsOpen(true);
    }
    onOpen?.();
  }

  function handleOnClose() {
    if (typeof props.isOpen === "undefined") {
      setIsOpen(false);
    }
    onClose?.();
  }

  function toggleOpen() {
    if (isOpen) {
      handleOnClose();
    } else {
      handleOnOpen();
    }
  }

  useEffect(() => {
    if (props.isOpen !== undefined && props.isOpen !== isOpen) {
      setIsOpen(props.isOpen);
    }
  }, [props.isOpen]);

  return (
    <>
      {!!props.target && (
        <Clickable
          className="kit-drawer-target"
          as="div"
          onClick={toggleOpen}
          role="switch"
        >
          {props.target}
        </Clickable>
      )}
      {!props.pushContent ? (
        <DrawerWithOverlay
          {...props}
          onClose={handleOnClose}
          onOpen={handleOnOpen}
          isOpen={isOpen}
          panelRef={panelRef}
        />
      ) : (
        <DrawerBase
          {...props}
          onClose={handleOnClose}
          onOpen={handleOnOpen}
          isOpen={isOpen}
          panelRef={panelRef}
        />
      )}
    </>
  );
}

/**
 * Base Drawer component that handles the rendering of the drawer panel and its content.
 * @internal
 */
function DrawerBase({
  isOpen,
  onClose,
  children,
  title,
  titleIcon,
  noHeader,
  headerButtons,
  footer,
  noPadding = false,
  noContentPadding = false,
  size = "small",
  topOffset = 0,
  position = "right",
  pushContent = false,
  panelRef: _panelRef,
  closeOnFooterClick,
  theme
}: InternalDrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const setCssVariable: DrawerSetCssVariable = name => val => {
    drawerRef.current?.style.setProperty(name, val);
  };

  useImperativeHandle(_panelRef, () => panelRef.current);

  return (
    <div
      className="kit-Drawer"
      ref={drawerRef}
      css={[
        styles.wrapper.base,
        styles.wrapper.size[size],
        css`
          --drawer-footer-height: 0px;
          --drawer-header-height: 0px;
          --drawer-top-offset: ${topOffset}px;
        `,
        pushContent &&
          css`
            ${styles.wrapper.withPushContent}
            ${styles.wrapper.positionWithPushContent[position]}
          `,
        !pushContent && styles.wrapper.withoutPushContent
      ]}
    >
      {!pushContent && (
        <Clickable
          className="kit-overlay-backdrop"
          as="div"
          css={styles.backdrop}
          onClick={onClose}
        />
      )}

      {!!pushContent && (
        <DrawerPushContent
          isOpen={!!isOpen}
          setContentMaxHeight={setCssVariable("--drawer-content-max-height")}
          setContentTopOffset={setCssVariable("--drawer-content-top-offset")}
        >
          {pushContent}
        </DrawerPushContent>
      )}

      <DrawerPanel
        ref={panelRef}
        isOpen={isOpen}
        position={position}
        pushContent={pushContent}
        onClose={onClose}
        title={title}
        titleIcon={titleIcon}
        headerButtons={headerButtons}
        footer={footer}
        noPadding={noPadding}
        noContentPadding={noContentPadding}
        noHeader={noHeader}
        setHeaderHeight={setCssVariable?.("--drawer-header-height")}
        setFooterHeight={setCssVariable?.("--drawer-footer-height")}
        closeOnFooterClick={closeOnFooterClick}
        theme={theme}
      >
        {children}
      </DrawerPanel>
    </div>
  );
}

/**
 * Overflow Drawer component that creates a portal to render the drawer outside of the normal DOM hierarchy.
 * Additionally, it listens for the Escape key to close the drawer when focus is outside the panel.
 * @internal
 */
function DrawerWithOverlay({
  portalTo = document.body,
  isOpen,
  panelRef: _panelRef,
  ...props
}: InternalDrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(_panelRef, () => panelRef.current);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (e: KeyboardEvent) => {
      // Only close if Esc is pressed AND focus is NOT inside the drawer panel
      if (
        e.key === "Escape" &&
        !panelRef.current?.contains(document.activeElement)
      ) {
        props.onClose?.();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <DrawerBase {...props} isOpen={isOpen} panelRef={panelRef} />,
    portalTo
  );
}

/**
 * Internal props for the Drawer component.
 * This type extends the public DrawerProps with additional internal properties,
 * used for managing the drawer's behavior and state.
 * @internal
 */
type InternalDrawerProps = DrawerProps & {
  /**
   * Reference to the drawer panel element.
   * @internal
   */
  panelRef?: React.RefObject<HTMLDivElement | null>;
};

type DrawerSetCssVariable = (name: DrawerCssVariable) => (val: string) => void;

/**
 * CSS variable names used in the Drawer component for dynamic styling.
 * @internal
 */
type DrawerCssVariable =
  | "--drawer-header-height"
  | "--drawer-footer-height"
  | "--drawer-content-max-height"
  | "--drawer-content-top-offset";

export default Drawer;
